import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { MatIcon } from '@angular/material/icon';

import { I18nModule, UrlModule } from '@spartacus/core';
import { GenericLinkModule } from '@spartacus/storefront';
import { firstValueFrom } from 'rxjs';

import { DpDeliveryCenter } from '@app/core/types/backend/dp-delivery-center.interface';
import { DP_STOCK_TYPE } from '@app/core/types/backend/dp-stock-type.enum';
import { DpStockInfoService } from '@app/ui-components/stock-info/stock-info.service';
import { DpTooltipDirective } from '@app/ui-components/tooltip';

@Component({
    selector: 'dp-stock-info',
    templateUrl: './stock-info.component.html',
    styleUrls: ['./stock-info.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [I18nModule, MatIcon, DpTooltipDirective, GenericLinkModule, UrlModule, NgTemplateOutlet]
})
export class DpStockInfoComponent {
  type = input.required<DP_STOCK_TYPE>();
  lifecycleStatus = input<string>();
  replenishmentDate = input<string>();
  productCode = input<string>();
  deliveryCenters = input<DpDeliveryCenter[] | null>();
  replacementProductCode = input<string>();
  isDefaultDC = input<boolean>(true);
  deliveryCentersSignal = signal<DpDeliveryCenter[] | null>(null);
  stockData = computed(() => this.deliveryCenters() ?? this.deliveryCentersSignal());

  stockTypes = DP_STOCK_TYPE;
  styleClass = computed<string>(() => this.type().toLowerCase());

  hasStockInDefaultDC = computed<boolean>(
    () => this.stockData()?.some((dc) => dc.isDefault && dc.stockLevel > 0) ?? false
  );

  stockInfo = computed(() =>
    (
      this.stockData()?.map((dc) => ({
        warehouse: dc.warehouse,
        stockLevel: dc.stockLevel,
        isDefault: dc.isDefault ?? false
      })) ?? []
    ).sort((a, b) => Number(b.isDefault) - Number(a.isDefault))
  );

  readonly #stockService = inject(DpStockInfoService);

  fetchStockDataIfNeeded = async (): Promise<void> => {
    const productCode = this.productCode() ?? '';
    if (this.productCode() && !this.stockData()) {
      try {
        const stockData = await firstValueFrom(this.#stockService.fetchStockData(productCode));
        this.deliveryCentersSignal.set(stockData);
      } catch (error) {
        console.error(error);
      }
    }
  };
}
